// pages/tasks/CompleteTicketModal.jsx
//
// "Mark Ticket Completed" -- My Complaints -> Own Tickets. Also doubles as
// the "Edit completion details" flow for an already-completed own ticket
// (same fields, pre-filled from the existing data) -- one component, not
// two, since the form shape is identical either way.
//
// Posts to PUT /tasks/:id/complete (controllers/taskController.js
// #completeOwnTask) -- a DEDICATED, owner-only endpoint, never the shared
// PUT /tasks/:id/status Admin/Staff flows use. Reuses the canonical
// COMPLETED task_statuses row and the existing completed_by/completed_at
// columns server-side; this component only collects the fields the backend
// doesn't already have: the actual completion date/time and the
// Resolution / Work Done text.
import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../api";
import "./TicketModalTheme.css";

function todayDateString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}
function nowTimeString() {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}
// Splits a "YYYY-MM-DD HH:mm:ss" (or ISO-ish) datetime string into its
// local date/time parts via a plain regex -- deliberately NOT `new
// Date(...)`, which would reinterpret the string in the browser's timezone
// and can shift the calendar date. The backend stores/returns this as a
// naive local timestamp (see controllers/taskController.js#completeOwnTask),
// so it's split as text, not re-parsed as an instant.
function splitLocalDateTime(value) {
  if (!value) return { date: "", time: "" };
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return match ? { date: match[1], time: match[2] } : { date: "", time: "" };
}

export default function CompleteTicketModal({ show, task, onClose, onCompleted }) {
  const isEdit = String(task?.status || "").toLowerCase() === "completed";

  const [completionDate, setCompletionDate] = useState(todayDateString());
  const [completionTime, setCompletionTime] = useState(nowTimeString());
  const [resolution, setResolution] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset/prefill every time the modal is (re)opened for a task -- never
  // carries stale values from a previously-opened ticket into this one.
  useEffect(() => {
    if (!show || !task) return;
    const existing = splitLocalDateTime(task.completed_at);
    setCompletionDate(existing.date || todayDateString());
    setCompletionTime(existing.time || nowTimeString());
    setResolution(task.completion_remark || "");
    setRemarks("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, task?.id]);

  const canSubmit = !!completionDate.trim() && !!resolution.trim();

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!canSubmit || !task) return;
    try {
      setSaving(true);
      const res = await api.put(`/tasks/${task.id}/complete`, {
        resolution: resolution.trim(),
        remarks: remarks.trim() || undefined,
        completion_date: completionDate,
        completion_time: completionTime,
      });
      toast.success(isEdit ? "Completion details updated" : "Ticket marked completed");
      onCompleted?.(res.data);
      onClose?.();
    } catch (err) {
      console.error("Complete ticket error:", err);
      toast.error(err.response?.data?.error || "Failed to mark ticket completed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered scrollable className="unit-modal ticket-modal-theme">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Edit Completion Details" : "Mark Ticket Completed"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row g-3">
          <div className="col-12">
            <Form.Group controlId="complete-ticket-summary">
              <Form.Label>Ticket</Form.Label>
              <Form.Control readOnly disabled value={task ? `#${task.id} — ${task.title || ""}` : ""} />
            </Form.Group>
          </div>

          <div className="col-12">
            <Form.Group controlId="complete-ticket-status">
              <Form.Label>Status</Form.Label>
              <Form.Control readOnly disabled value="Completed" />
            </Form.Group>
          </div>

          <div className="col-md-6">
            <Form.Group controlId="complete-ticket-date">
              <Form.Label>Completion Date <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="date"
                max={todayDateString()}
                value={completionDate}
                onChange={(e) => setCompletionDate(e.target.value)}
              />
            </Form.Group>
          </div>

          <div className="col-md-6">
            <Form.Group controlId="complete-ticket-time">
              <Form.Label>Completion Time</Form.Label>
              <Form.Control
                type="time"
                value={completionTime}
                onChange={(e) => setCompletionTime(e.target.value)}
              />
            </Form.Group>
          </div>

          <div className="col-12">
            <Form.Group controlId="complete-ticket-resolution">
              <Form.Label>Resolution / Work Done <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Describe what was actually done to resolve this ticket"
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
              />
            </Form.Group>
          </div>

          <div className="col-12">
            <Form.Group controlId="complete-ticket-remarks">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional additional remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Form.Group>
          </div>
        </div>

        <small className="text-muted d-block mt-3">Fields marked with * are required. Completion date cannot be in the future.</small>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving || !canSubmit}
        >
          {saving ? "Saving..." : isEdit ? "Save Changes" : "Mark Completed"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
